import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  Loader2, FileText, Upload, X, Trash2, FolderUp, Library,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axiosInstance from '../../api/axios';
import { validateFileSizes } from '../../utils/controlConstants';
import FileDropzone from '../FileDropzone';
import { FolderUploadDialog, LibraryPicker } from '../../features/evidence-library/index.ts';
import { EvidenceFolderTable } from '../../features/evidence-library/components/EvidenceFolderTable.tsx';
import { RemoveEvidenceDialog } from '../../features/evidence-library/components/RemoveEvidenceDialog.tsx';
import { linkLibraryFiles } from '../../request/evidence-library/evidenceLibraryRequests.ts';
import { useInvalidateLibrary } from '../../hooks/evidence-library/useEvidenceLibrary.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

// ─── helpers ────────────────────────────────────────────────────────────────

const formatPeriodRange = (start, end) => {
  if (!start || !end) return 'Evergreen';
  const s = parseISO(String(start).split('T')[0]);
  const e = parseISO(String(end).split('T')[0]);
  return `${format(s, 'MMM d')} — ${format(e, 'MMM d')}, ${format(e, 'yyyy')}`;
};

// ─── upload modal ────────────────────────────────────────────────────────────

function UploadModal({ controlUuid, evidencePeriods, onClose, onUploaded }) {
  // Files join the evidence library in the audit year the user is working in.
  const { selectedAuditYearId } = useAuditYear();
  const [evidenceType,  setEvidenceType]  = useState('pbc');
  const [periodStart,   setPeriodStart]   = useState('');
  const [periodEnd,     setPeriodEnd]     = useState('');
  const [uploading,     setUploading]     = useState(false);

  const existingDated = evidencePeriods.filter(p => p.testing_period_end);
  const smartStart = (() => {
    if (existingDated.length === 0) return new Date().toISOString().split('T')[0];
    const lastEnd = existingDated.reduce((latest, p) => {
      const d = String(p.testing_period_end).split('T')[0];
      return d > latest ? d : latest;
    }, '');
    if (!lastEnd) return new Date().toISOString().split('T')[0];
    const next = new Date(lastEnd + 'T00:00:00Z');
    next.setUTCDate(next.getUTCDate() + 1);
    return next.toISOString().split('T')[0];
  })();

  const selectValue =
    periodStart === '__evergreen__' ? '__evergreen__'
    : (periodStart && periodEnd && evidencePeriods.some(p => {
        const ps = String(p.testing_period_start).split('T')[0];
        const pe = String(p.testing_period_end).split('T')[0];
        return ps === periodStart && pe === periodEnd;
      })) ? `${periodStart}_${periodEnd}`
    : periodStart ? '__new__'
    : '';

  const periodReady =
    periodStart === '__evergreen__' ||
    (periodStart && periodEnd && periodEnd >= periodStart);

  const typeHint = {
    pbc:               'File is mapped to one or more test steps by the AI.',
    population_report: 'File is counted deterministically; drives the sample-size recommendation.',
    verification:      'File is consumed by the Completeness & Accuracy agents. Not mapped to a step.',
  }[evidenceType] || '';

  const handleFiles = async (files) => {
    const sizeErrors = validateFileSizes(files);
    if (sizeErrors.length > 0) { toast.error(sizeErrors.join(' ')); return; }
    setUploading(true);
    const userHash = localStorage.getItem('userHash');
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      if (periodStart !== '__evergreen__') {
        fd.append('testing_period_start', periodStart);
        fd.append('testing_period_end', periodEnd);
      }
      fd.append('evidence_type', evidenceType);
      if (selectedAuditYearId) fd.append('audit_year_id', selectedAuditYearId);
      try {
        await axiosInstance.post(`/api/sox-controls/${controlUuid}/evidence/upload`, fd, {
          headers: { Authorization: userHash || '', 'Content-Type': 'multipart/form-data' },
        });
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setUploading(false);
    onUploaded();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4"
        onClick={e => e.stopPropagation()}>

        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Upload Evidence</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Evidence type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload as</label>
            <select
              value={evidenceType}
              onChange={e => setEvidenceType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="pbc">Evidence (PBC) — mapped to test steps</option>
              <option value="population_report">Population report — counted for sampling</option>
              <option value="verification">Verification (GL, raw export, source docs) — C&amp;A checks</option>
            </select>
            {typeHint && <p className="text-[11px] text-gray-500 mt-1">{typeHint}</p>}
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Testing Period</label>
            <select
              value={selectValue}
              onChange={e => {
                const val = e.target.value;
                if (val === '__new__')       { setPeriodStart(smartStart); setPeriodEnd(''); }
                else if (val === '__evergreen__') { setPeriodStart('__evergreen__'); setPeriodEnd('__evergreen__'); }
                else if (val) { const [s, en] = val.split('_'); setPeriodStart(s); setPeriodEnd(en); }
                else          { setPeriodStart(''); setPeriodEnd(''); }
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="">Choose a period…</option>
              {evidencePeriods.filter(p => p.testing_period_start && p.testing_period_end).map((p, i) => {
                const ps = String(p.testing_period_start).split('T')[0];
                const pe = String(p.testing_period_end).split('T')[0];
                return (
                  <option key={i} value={`${ps}_${pe}`}>
                    {formatPeriodRange(p.testing_period_start, p.testing_period_end)} ({p.file_count} files)
                  </option>
                );
              })}
              <option value="__evergreen__">Evergreen (all periods)</option>
              <option value="__new__">+ Create new period</option>
            </select>
          </div>

          {periodStart && periodStart !== '__evergreen__' && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                <input type="date" value={periodStart}
                  onChange={e => setPeriodStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                <input type="date" value={periodEnd}
                  onChange={e => setPeriodEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
            </div>
          )}

          {periodStart && periodEnd && periodStart !== '__evergreen__' && periodEnd < periodStart && (
            <p className="text-sm text-red-600">End date must be on or after the start date.</p>
          )}

          {periodReady && (
            <FileDropzone
              multiple
              isUploading={uploading}
              label="Drop evidence files here or click to browse"
              compact
              onFilesSelected={handleFiles}
            />
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EvidenceStoreTab({ controlUuid, onPreviewFile }) {
  const [evidence,    setEvidence]    = useState([]);
  const [periods,     setPeriods]     = useState([]);
  const [execs,       setExecs]       = useState([]);
  const [usage,       setUsage]       = useState({});
  const [controlCode, setControlCode] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [search,      setSearch]      = useState('');
  const [showUpload,  setShowUpload]  = useState(false);
  // Files to remove ({ rows, title }): one file, the selection, or a folder.
  const [toRemove, setToRemove] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectIds = (ids, select) => setSelectedIds(prev => {
    const next = new Set(prev);
    ids.forEach(id => (select ? next.add(id) : next.delete(id)));
    return next;
  });
  const [showFolderUpload, setShowFolderUpload] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const invalidateLibrary = useInvalidateLibrary();
  const { selectedAuditYearId } = useAuditYear();

  // Library picks from this tab are linked to the control with no testing period (evergreen).
  const linkFromLibrary = async (picks) => {
    const results = await linkLibraryFiles({
      sox_control_id: controlUuid,
      items: picks.map(p => ({ control_upload_id: p.file.id, file_category: p.role })),
    });
    const failed = results.filter(r => r.status === 'error');
    if (failed.length) toast.error(`${failed.length} file${failed.length > 1 ? 's' : ''} could not be added: ${failed[0].error}`);
    setShowLibrary(false);
    invalidateLibrary();
    fetchAll();
  };

  const fetchAll = useCallback(async () => {
    if (!controlUuid) return;
    setLoading(true);
    const userHash = localStorage.getItem('userHash');
    try {
      const [ev, pe, ex, us] = await Promise.all([
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`).then(r => r.data).catch(() => []),
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence/periods`).then(r => r.data).catch(() => []),
        axiosInstance.get(`/api/test-executions/control/${controlUuid}?include_history=1`, {
          headers: { Authorization: userHash || '' },
        }).then(r => r.data).catch(() => []),
        // Runs, mapping and other controls per file, from the control-testing tables.
        axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence/usage`).then(r => r.data).catch(() => null),
      ]);
      setEvidence(Array.isArray(ev) ? ev : []);
      setUsage(us?.usage || {});
      setControlCode(us?.control_id || '');
      setPeriods(Array.isArray(pe) ? pe : []);
      setExecs(Array.isArray(ex) ? ex : []);
    } finally {
      setLoading(false);
    }
  }, [controlUuid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Classic test executions record the file names they analysed: filename → run labels.
  const legacyUsage = {};
  execs.forEach(ex => {
    (ex.files_analyzed || []).forEach(fn => {
      const name = typeof fn === 'string' ? fn : fn?.filename || fn?.name;
      if (!name) return;
      const label = `Classic RUN-${ex.test_run_number ?? '?'}`;
      legacyUsage[name] = Array.from(new Set([...(legacyUsage[name] || []), label]));
    });
  });

  // Apply period filter then search
  const filtered = useMemo(() => evidence.filter(ev => {
    if (periodFilter !== 'all') {
      if (periodFilter === 'evergreen') {
        if (ev.testing_period_start || ev.testing_period_end) return false;
      } else {
        const [fs, fe] = periodFilter.split('_');
        const es = ev.testing_period_start ? String(ev.testing_period_start).split('T')[0] : '';
        const ee = ev.testing_period_end   ? String(ev.testing_period_end).split('T')[0]   : '';
        if (es !== fs || ee !== fe) return false;
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return ev.filename?.toLowerCase().includes(q) || ev.relative_path?.toLowerCase().includes(q);
    }
    return true;
  }), [evidence, periodFilter, search]);

  // Only files you can see stay selected: a filter change or a refresh must not leave hidden files queued for removal.
  useEffect(() => {
    const visible = new Set(filtered.map(ev => ev.id));
    setSelectedIds(prev => {
      const next = new Set(Array.from(prev).filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const totalFiles = evidence.length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Evidence Store</h2>
          <p className="text-sm text-gray-500">
            All evidence for this control — type, period, mapping status and test run usage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLibrary(true)}
            className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50 inline-flex items-center gap-2"
          >
            <Library className="h-4 w-4" />
            Add from library
          </button>
          <button
            onClick={() => setShowFolderUpload(true)}
            className="px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50 inline-flex items-center gap-2"
          >
            <FolderUp className="h-4 w-4" />
            Upload folder
          </button>
          <button
            onClick={() => setShowUpload(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload Evidence
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm">
          <span className="font-medium text-blue-900">{selectedIds.size} file{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <button
            onClick={() => setToRemove({ rows: filtered.filter(ev => selectedIds.has(ev.id)), title: `Remove ${selectedIds.size} file${selectedIds.size !== 1 ? 's' : ''}` })}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
          >
            <Trash2 className="h-3.5 w-3.5" /> Remove…
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="text-xs text-blue-800 underline">Clear selection</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-600">Period:</label>
          <select
            value={periodFilter}
            onChange={e => setPeriodFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none min-w-[200px]"
          >
            <option value="all">All Periods ({totalFiles} files)</option>
            {periods.map((p, i) => {
              const key = p.testing_period_start && p.testing_period_end
                ? `${String(p.testing_period_start).split('T')[0]}_${String(p.testing_period_end).split('T')[0]}`
                : 'evergreen';
              const label = key === 'evergreen'
                ? `Evergreen (${p.file_count})`
                : `${formatPeriodRange(p.testing_period_start, p.testing_period_end)} (${p.file_count})`;
              return <option key={i} value={key}>{label}</option>;
            })}
          </select>
        </div>
        <div className="flex-1 max-w-xs">
          <input
            type="text"
            placeholder="Search files or folders…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading evidence…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          {evidence.length === 0 ? (
            <>
              <p className="text-sm font-medium text-gray-700 mb-1">No evidence uploaded yet</p>
              <p className="text-xs text-gray-500 mb-4">Upload files using the button above.</p>
              <button
                onClick={() => setShowUpload(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              >
                <Upload className="h-4 w-4" /> Upload Evidence
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500">No files match the current filter.</p>
          )}
        </div>
      ) : (
        <EvidenceFolderTable
          files={filtered}
          usage={usage}
          controlCode={controlCode}
          legacyUsage={legacyUsage}
          searching={Boolean(search)}
          formatPeriod={formatPeriodRange}
          onPreview={ev => onPreviewFile?.({ id: ev.control_upload_id, name: ev.filename })}
          onRemove={(rows, folderName) => setToRemove({
            rows,
            title: folderName ? `Remove folder "${folderName}"` : rows.length === 1 ? `Remove ${rows[0].filename}` : `Remove ${rows.length} files`,
          })}
          selected={selectedIds}
          onSelect={selectIds}
        />
      )}

      {/* Modals */}
      {showUpload && (
        <UploadModal
          controlUuid={controlUuid}
          evidencePeriods={periods}
          onClose={() => setShowUpload(false)}
          onUploaded={fetchAll}
        />
      )}
      {showFolderUpload && (
        <FolderUploadDialog
          title="Upload folder to this control"
          context={{ scope: 'control', soxControlId: controlUuid }}
          onClose={() => setShowFolderUpload(false)}
          onUploaded={() => fetchAll()}
        />
      )}
      {showLibrary && (
        <LibraryPicker
          soxControlId={controlUuid}
          auditYearId={selectedAuditYearId}
          roles={['evidence', 'population', 'ipe_proof', 'supporting']}
          linkedUploadIds={new Set(evidence.map(ev => ev.control_upload_id))}
          onConfirm={linkFromLibrary}
          onClose={() => setShowLibrary(false)}
        />
      )}
      {toRemove && (
        <RemoveEvidenceDialog
          controlUuid={controlUuid}
          files={toRemove.rows}
          usage={usage}
          title={toRemove.title}
          onClose={() => setToRemove(null)}
          onDone={() => {
            const removed = new Set(toRemove.rows.map(r => r.id));
            setSelectedIds(prev => new Set(Array.from(prev).filter(id => !removed.has(id))));
            setToRemove(null);
            fetchAll();
          }}
        />
      )}
    </div>
  );
}
